import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StyledWrapper, StyledSlider, StyledActiveSlider, StyledCursor } from './style';

/**
 * @doc Component
 * @component Slider.Horizon
 *
 * @param {Number} value 현재 값
 * @param {Number} max 최대 값
 * @param {Number} min 최소 값
 * @param {Boolean} disabled 비활성화 여부
 * @param {Boolean} bind 양방향 바인딩 여부
 * @param {Function} setValue
 *
 * @description
 * bind 옵션을 적용할 경우 value 값에 의존하여 cursor 가 동작한다.
 *
 * ```
 * <Slider.Hrizon min={0} max={100} value={slider} setValue={(value) => setSlider(value)}/>
 * ```
 */

export interface Props {
  bind?: boolean;
  value: number;
  resizeObserverElement?: HTMLElement | null;
  min: number;
  max: number;
  disabled?: boolean;
  setValue?: (value: number) => void;
}

const Slider = React.memo(({ max, min, value, disabled, resizeObserverElement, setValue, bind = false }: Props) => {
  const resiveObserver = useRef<ResizeObserver | null>(null);
  const slider = useRef<HTMLDivElement>(null);
  const cursor = useRef<HTMLDivElement>(null);
  const isDragging = useRef<boolean>(false);
  const isDirectionClick = useRef<boolean>(false);

  const [left, setLeft] = useState(0);

  const diff = useMemo(() => max - min, [max, min]);

  /**
   * docuemnt 에서 mouseup 했을 때 동작
   */
  const mouseUpHandler = () => {
    isDragging.current = false;
    document.removeEventListener('mousemove', mouseMoveHandler);
    document.removeEventListener('mouseup', mouseUpHandler);
  };

  /**
   * cursor 에 mousedown 했을 때 동작
   */
  const mouseDownHandler = () => {
    isDragging.current = true;
    document.addEventListener('mousemove', mouseMoveHandler);
    document.addEventListener('mouseup', mouseUpHandler);
  };

  /**
   * clickToDirection 시 mouseup 이벤트로 인한 isDirectionClick 해제
   */
  const removeIsDirectionClickStatus = () => {
    isDirectionClick.current = false;
  };

  /**
   * UI, value 업데이트
   * @param {number} x
   * @param {boolean} isDirect 마우스 클릭 이벤트로 slider 동작시 true (default false)
   * @returns
   */
  const update = (x: number, isDirect = false) => {
    // direct가 아닌데 dragging 하지 않은 경우 return
    if (!isDirect && !isDragging.current) return;
    if (disabled || !slider.current || !cursor.current) return;

    const bound = slider.current.getBoundingClientRect();
    const sliderWidth = slider.current.clientWidth;
    const cursorWidth = cursor.current.clientWidth;

    // UI 계산
    const maxX = sliderWidth - cursorWidth; // 최대로 움직일 수 있는 left 거리
    let offsetLeft = x - bound.x; // 현재 left 위치

    // 커서 중앙
    offsetLeft = offsetLeft - cursor.current.clientWidth / 2;

    if (offsetLeft <= 0) offsetLeft = 0;
    else if (offsetLeft >= maxX) offsetLeft = maxX;

    if (!bind) setLeft(offsetLeft);

    // value 계산
    const newValue = Math.round(offsetLeft / ((sliderWidth - cursorWidth) / diff)) + min;
    setValue && setValue(newValue);
  };

  /**
   * 브라우저 화면에서 커서가 x 축 이동을 할때 영향을 받는다.
   */
  const mouseMoveHandler = (ev: MouseEvent) => {
    update(ev.x);
  };

  /**
   * 동적 움직임에 대한 대응
   */
  const observed = useCallback(() => {
    const bindCondition = !bind && (isDirectionClick.current || isDragging.current);
    if (bindCondition || !slider.current || !cursor.current) return;
    if (diff === 0) return;

    const sliderWidth = slider.current.clientWidth;
    const cursorWidth = cursor.current.clientWidth;

    const step = (sliderWidth - cursorWidth) / diff;
    const term = step * min;

    let next = value < min ? min : value;
    next = value > max ? max : next;

    const offsetLeft = parseFloat(((sliderWidth - cursorWidth) * (next / diff) - term).toFixed(2));

    setLeft(offsetLeft);
    isDirectionClick.current = false;
  }, [value, min, max, diff]);

  /**
   * slider 무작위 위치 클릭시 이동
   * @param ev
   */
  const clickToDirection = (ev: React.MouseEvent<HTMLDivElement>) => {
    // 직접 클릭시 cursor가 오동작을 하기 때문에 observed 막기용도
    isDirectionClick.current = true;
    update(ev.clientX, true);

    // direction click cursor 움직임을 줄 수 있기 때문에 함수 실행
    mouseDownHandler();

    // direction click 0.5초 간 움직임이 없는 경우 자동 해제
    setTimeout(() => {
      isDirectionClick.current = false;
    }, 500);
  };

  /** value가 외부에서 변경된 경우 자동으로 left 조정 */
  useEffect(() => {
    observed();

    // 특정 element layout 변형시 resize observer 실행
    if (!resizeObserverElement) return;
    if (resiveObserver.current) resiveObserver.current.disconnect();

    resiveObserver.current = new ResizeObserver(() => {
      observed();
    });

    resiveObserver.current.observe(resizeObserverElement);
  }, [resizeObserverElement, observed]);

  /** window resize 이벤트에 동작*/
  useEffect(() => {
    window.addEventListener('resize', observed);
    window.addEventListener('mouseup', removeIsDirectionClickStatus);
    return function cleanup() {
      window.removeEventListener('resize', observed);
      window.removeEventListener('mouseup', removeIsDirectionClickStatus);
    };
  }, [observed]);

  return (
    <StyledWrapper className={disabled ? 'disabled' : ''}>
      <StyledSlider ref={slider} className="slider" onMouseDown={clickToDirection}>
        <StyledActiveSlider className="active" style={{ width: left + 6 }} />
        <StyledCursor ref={cursor} className="cursor" onMouseDown={mouseDownHandler} style={{ left }} />
      </StyledSlider>
    </StyledWrapper>
  );
});

Slider.displayName = 'Slider';

export default Slider;
